import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Grid } from '@mui/material';
import { FiltersContainer } from './FilterPanel.style';
import {
  BooleanFilter,
  ArrayFilter,
  GroupedArrayFilter,
} from './FormInputControls';
import { AppliedFilters } from './AppliedFilters';
import { normalizeFilterName } from './__feature__';
import type { ISearchFilter } from '../common-types/filter';

export interface FilterPanelProps {
  filters: ISearchFilter[];
  isLoading?: boolean;
  testId?: string;
  preAppliedFilters?: Array<{ filterName: string; selectedOptions: string[] }>;
  onFiltersChangeCallback: (
    filters: Array<{ filterName: string; selectedOptions: string[] }>
  ) => void;
}

/**
 * FilterPanel component from [MicroLibrarySearchUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibrarySearchUI)
 * @param filters - The Filter used to generate the FilterPanel
 * @param preAppliedFilters - The already applied filters
 * @param isLoading - Show the loading animation
 * @param testId - The test id used on the FiltersContainer
 * @param onFiltersChangeCallback - The call back function, that is used to update/remove the filters
 */

export const FilterPanel = ({
  filters,
  preAppliedFilters = [],
  isLoading = false,
  onFiltersChangeCallback,
  testId = 'filter-panel-container',
}: FilterPanelProps) => {
  const [appliedFilters, setAppliedFilters] = useState<
    Array<{
      filterName: string;
      selectedOptions: string[];
    }>
  >([]);

  const updateAppliedFilters = useCallback(
    (filterName: string, selectedOptions: string[]) => {
      try {
        setAppliedFilters((prev) => {
          const existingIndex = prev.findIndex(
            (f) =>
              normalizeFilterName(f?.filterName) ===
              normalizeFilterName(filterName)
          );
          const newFilters =
            existingIndex > -1
              ? prev.map((filter, index) =>
                  index === existingIndex
                    ? { filterName, selectedOptions }
                    : filter
                )
              : [...prev, { filterName, selectedOptions }];
          onFiltersChangeCallback(newFilters);
          return newFilters;
        });
      } catch (error) {
        console.error('Error updating applied filters', error);
      }
    },
    [onFiltersChangeCallback]
  );

  const removeAppliedFilter = useCallback(
    (filterName: string, option: string) => {
      try {
        setAppliedFilters((prev) => {
          const newFilters = prev
            .map((filter) =>
              normalizeFilterName(filter?.filterName) ===
              normalizeFilterName(filterName)
                ? {
                    ...filter,
                    selectedOptions: filter.selectedOptions.filter(
                      (opt) => opt !== option
                    ),
                  }
                : filter
            )
            .filter((filter) => filter.selectedOptions.length > 0);

          onFiltersChangeCallback(newFilters);
          return newFilters;
        });
      } catch (error) {
        console.error('Error removing applied filter', error);
      }
    },
    [onFiltersChangeCallback]
  );

  const clearAppliedFilters = useCallback(() => {
    try {
      setAppliedFilters([]);
      onFiltersChangeCallback?.([]);
    } catch (error) {
      console.error('Error clearing applied filters', error);
    }
  }, [onFiltersChangeCallback]);

  const evaluateFilterName = useCallback((a: string, b: string): boolean => {
    const aParts = a.toLowerCase().trim();
    const bParts = b.toLowerCase().trim();
    return aParts === bParts;
  }, []);

  const mapSelectedOptions = useCallback(
    (selectedOptions: string[], filter: ISearchFilter) => {
      if (filter.dataType === 'StringArray') {
        if (!filter.filterValues) return [];

        return selectedOptions.map((selectedValue) => {
          const foundValue = filter.filterValues.find(
            (f) =>
              f.value === selectedValue || f.translatedValue === selectedValue
          );
          return (
            foundValue ?? {
              value: selectedValue,
              translatedValue: selectedValue,
            }
          );
        });
      }
      return [];
    },
    []
  );

  useEffect(() => {
    if (preAppliedFilters.length) {
      setAppliedFilters(preAppliedFilters);
    }
  }, [preAppliedFilters]);

  const booleans = useMemo(
    () => filters?.filter((filter) => filter.dataType === 'Boolean'),
    [filters]
  );

  const arrays = useMemo(
    () => filters?.filter((filter) => filter.dataType !== 'Boolean'),
    [filters]
  );

  return (
    <Box display="grid" gap={2}>
      {isLoading ? (
        <CircularProgress
          sx={{
            margin: 'auto',
          }}
          size={30}
          data-testid="filter-panel-loading"
        />
      ) : (
        <>
          <FiltersContainer data-testid={testId}>
            {arrays?.map((filter: ISearchFilter) => {
              if (filter.dataType === 'GroupedArray') {
                return (
                  <GroupedArrayFilter
                    key={normalizeFilterName(filter.filterName)}
                    filter={filter}
                    selectedOptions={
                      appliedFilters?.find((a) =>
                        evaluateFilterName(a.filterName, filter.filterName)
                      )?.selectedOptions ?? []
                    }
                    handleSelectedChange={(selectedOptions: string[]) => {
                      updateAppliedFilters(filter.filterName, selectedOptions);
                    }}
                    testId={`filters-grouped-filter-${normalizeFilterName(
                      filter.filterName
                    )}`}
                  />
                );
              }
              if (filter.dataType === 'StringArray') {
                return (
                  <ArrayFilter
                    key={filter.filterName}
                    filter={filter}
                    selectedOptions={
                      appliedFilters?.find((a) =>
                        evaluateFilterName(a.filterName, filter.filterName)
                      )?.selectedOptions ?? []
                    }
                    handleSelectedChange={(selectedOptions) => {
                      const mappedSelectedOptions = mapSelectedOptions(
                        selectedOptions,
                        filter
                      );
                      updateAppliedFilters(
                        filter.filterName,
                        mappedSelectedOptions.map((opt) => opt.value)
                      );
                    }}
                    testId={`filters-string-array-${normalizeFilterName(
                      filter.filterName
                    )}`}
                  />
                );
              }
              return <></>;
            })}
          </FiltersContainer>
          {booleans?.map((filter: ISearchFilter) => {
            return (
              <BooleanFilter
                key={filter.filterName}
                filter={filter}
                selectedOptions={
                  appliedFilters?.find((a) =>
                    evaluateFilterName(a.filterName, filter.filterName)
                  )?.selectedOptions ?? []
                }
                handleSelectedChange={(selectedOptions: string[]) => {
                  updateAppliedFilters(filter.filterName, selectedOptions);
                }}
                testId={`filters-boolean-filter-${normalizeFilterName(
                  filter.filterName
                )}`}
              />
            );
          })}
          <Grid item xs={12}>
            <Box>
              <AppliedFilters
                appliedFilters={appliedFilters}
                removeAppliedFilter={removeAppliedFilter}
                clearAppliedFilters={clearAppliedFilters}
                filters={filters}
              />
            </Box>
          </Grid>
        </>
      )}
    </Box>
  );
};
